import React, { useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import Panel from '@wui/layout/panel';
import Button from '@wui/input/button';
import Spacer from '@wui/layout/spacer';
import Typography from '@wui/basics/typography';
import Link from '@willing-shared/components/Link';

import { STATES } from 'utils';

import { useGlobalContext } from 'hooks';

import ExitModal from './ExitModal';

// These states require licensed attorneys to prepare deeds/trusts
const ATTY_STATES = ['MA'];

const useStyles = makeStyles({
  footnote: {
    color: '#a9adb1 !important',
  },
});

const BundleBox = () => {
  const classes = useStyles();
  const { clientData, serverData } = useGlobalContext();
  const [showExitModal, setShowExitModal] = useState(false);

  const hasExempt = clientData.realEstates.some(a => ATTY_STATES.includes(a.address.state));
  const hasNonExempt = clientData.realEstates.some(a => !ATTY_STATES.includes(a.address.state));

  // The specific states the user has property in
  const exemptStates = clientData.realEstates.reduce((acc, asset) => {
    const { state } = asset.address;
    if (ATTY_STATES.includes(state) && !acc.includes(state)) {
      acc.push(state);
    }
    return acc;
  }, []);

  const planIncludesProbate =
    serverData && serverData.experienceConfig && serverData.experienceConfig.includesProbate;

  if (planIncludesProbate) {
    if (hasExempt && !hasNonExempt) {
      return (
        <>
          <Panel lessPadding>
            <Typography variant="body1">LAST STEP: PROTECT YOUR REAL ESTATE</Typography>

            <Spacer v={8} />

            {exemptStates.map(state => (
              <>
                <Spacer v={8} />
                <Typography key={state}>
                  To fully protect your real estate, transfer your assets into your trust using a
                  Home Transfer Deed. This process is called "funding your trust." {STATES[state]}{' '}
                  requires an attorney licensed in the {state === 'MA' ? 'Commonwealth' : 'State'}{' '}
                  of {STATES[state]} to prepare your home transfer deed. Please{' '}
                  <Link href="https://members.legalplans.com/directory?selected=Deed">
                    contact an attorney
                  </Link>{' '}
                  to prepare a home transfer deed for your real estate asset in {STATES[state]}.
                  <sup>1</sup>
                </Typography>
              </>
            ))}
            <Typography className={classes.footnote}>
              <small>
                <sup>1 </sup>
                If you are not re-enrolling in the Legal Plan, make sure to complete the deed
                preparation before your enrollment ends. If you have questions, please call us at
                833-946-2005.
              </small>
            </Typography>

            <Spacer v={8} />
          </Panel>
          <ExitModal open={showExitModal} onClose={() => setShowExitModal(false)} />
        </>
      );
    } else if (hasExempt && hasNonExempt) {
      return (
        <>
          <Panel lessPadding>
            <Typography variant="body1">LAST STEP: PROTECT YOUR REAL ESTATE</Typography>

            <Spacer v={8} />
            {exemptStates.map(state => (
              <>
                <Spacer v={8} />
                <Typography key={state}>
                  To fully protect your real estate, transfer your assets into your trust using a
                  Home Transfer Deed. This process is called "funding your trust." {STATES[state]}{' '}
                  requires an attorney licensed in the {state === 'MA' ? 'Commonwealth' : 'State'}{' '}
                  of {STATES[state]} to prepare your home transfer deed. Please{' '}
                  <Link href="https://members.legalplans.com/directory?selected=Deed">
                    contact an attorney
                  </Link>{' '}
                  to prepare a home transfer deed for your real estate asset in {STATES[state]}.
                </Typography>
              </>
            ))}

            <Spacer v={8} />
            <Typography>
              For your other real estate asset(s), transferring your real estate can be completed
              online with Bundle’s services, and the deed preparation costs are covered by your
              Legal Plan.<sup>1</sup> <sup>2</sup>
            </Typography>
            <Spacer v={8} />
            <Typography className={classes.footnote}>
              <small>
                <sup>1 </sup>
                Additional services such as deed notarization and deed recording (online or with a
                network attorney) are not included in your Legal Plan
              </small>
            </Typography>
            <Typography className={classes.footnote}>
              <small>
                <sup>2 </sup>
                If you are not re-enrolling in the Legal Plan, make sure to complete the deed
                preparation through Bundle before your enrollment ends. If you have questions,
                please call us at 833-946-2005.
              </small>
            </Typography>

            <Spacer v={8} />
            <Spacer v={16} />
            <Button
              color="primary"
              variant="contained"
              noMinWidth
              onClick={() => setShowExitModal(true)}
            >
              Continue to Bundle
            </Button>
          </Panel>
          <ExitModal open={showExitModal} onClose={() => setShowExitModal(false)} />
        </>
      );
    } else if (!hasExempt && hasNonExempt) {
      return (
        <>
          <Panel lessPadding>
            <Typography variant="body1">LAST STEP: PROTECT YOUR REAL ESTATE</Typography>

            <Spacer v={8} />

            <Spacer v={8} />
            <Typography>
              To fully protect your real estate, transfer your assets into your trust using a Home
              Transfer Deed. This process, called "funding your trust," can be easily done online
              with Bundle's services, and the deed preparation costs are covered by your Legal Plan.
              <sup>1 </sup>
              <sup>2</sup>
            </Typography>
            <Spacer v={8} />

            <Typography className={classes.footnote}>
              <small>
                <sup>1 </sup>
                Additional services such as deed notarization and deed recording (online or with a
                network attorney) are not included in your Legal Plan
              </small>
            </Typography>
            <Typography className={classes.footnote}>
              <small>
                <sup>2 </sup>
                If you are not re-enrolling in the Legal Plan, make sure to complete the deed
                preparation through Bundle before your enrollment ends. If you have questions,
                please call us at 833-946-2005.
              </small>
            </Typography>
            <Spacer v={8} />
            <Spacer v={16} />
            <Button
              color="primary"
              variant="contained"
              noMinWidth
              onClick={() => setShowExitModal(true)}
            >
              Continue to Bundle
            </Button>
          </Panel>
          <ExitModal open={showExitModal} onClose={() => setShowExitModal(false)} />
        </>
      );
    }
    return null;
  } else {
    return (
      <>
        <Panel lessPadding>
          <Typography variant="body1">LAST STEP: PROTECT YOUR REAL ESTATE</Typography>

          <Spacer v={8} />

          <Typography>
            To fully protect your real estate, transfer your assets into your trust using a Home
            Transfer Deed and record the deed with the local authorities. This process is called
            "funding your trust.”
          </Typography>

          <Spacer v={8} />
        </Panel>
        <ExitModal open={showExitModal} onClose={() => setShowExitModal(false)} />
      </>
    );
  }
};

export default BundleBox;
